import { AlertTriangle, Building2, ClipboardList, Copy, Pencil, Plus, Save, ShieldCheck, TrendingUp, Wallet, Wrench, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { DebtEvent, DebtRecord, FinancialInputs, ModelResult, Property, PropertyAuditRecord, PropertyDebtLink, PropertyRiskProfile, SavedModelScenario, ScenarioLineItem, ScenarioLineItemType, ScenarioLineItemValueType } from '../types';
import { formatCurrency0, formatNumber2 } from '../utils/format';
import InfoTooltip from './InfoTooltip';
import PropertyRiskProfileEditor from './PropertyRiskProfile';
import RiskActionQueue from './RiskActionQueue';
import { computeRiskSummary } from '../services/risk';
import { buildModel } from '../services/financials';
import { buildDebtSnapshotForProperty } from '../services/debt';
import { createEmptyScenarioLineItem, getScenarioAnalysisYear, getScenarioLineItemLabel, isScenarioLineItemValid } from '../services/scenarios';

interface RiskAnalysisViewProps {
  modelResult: ModelResult | null;
  inputs: FinancialInputs | null;
  riskProfile: PropertyRiskProfile | null;
  savedScenarios: SavedModelScenario[];
  auditLog: PropertyAuditRecord[];
  baseYear: number;
  activePropertyId?: string | null;
  propertyName?: string;
  properties?: Property[];
  debts?: Record<string, DebtRecord>;
  debtLinks?: PropertyDebtLink[];
  debtEvents?: DebtEvent[];
  onRiskProfileChange: (riskProfile: PropertyRiskProfile) => void;
  onSaveScenario: (scenario: SavedModelScenario) => void;
  onDeleteScenario: (scenarioId: string) => void;
  onRunScenario: (scenarioId: string) => void;
}

const getRiskTone = (score: number) => {
  if (score >= 70) return { label: 'High', className: 'text-rose-300 bg-rose-500/15 border-rose-500/40' };
  if (score >= 45) return { label: 'Moderate', className: 'text-amber-300 bg-amber-500/15 border-amber-500/40' };
  return { label: 'Low', className: 'text-emerald-300 bg-emerald-500/15 border-emerald-500/40' };
};

const scenarioTypeOptions: Array<{ value: ScenarioLineItemType; label: string; helper: string }> = [
  { value: 'expense', label: 'Expense', helper: 'Adds a generic operating expense without touching the base model.' },
  { value: 'repair', label: 'Repair', helper: 'Models a repair or capital hit in the selected year or year range.' },
  { value: 'tax_increase', label: 'Tax Change', helper: 'Adjusts property taxes by dollars or percentage.' },
  { value: 'insurance', label: 'Insurance Change', helper: 'Adjusts insurance by dollars or percentage.' },
  { value: 'rent_change', label: 'Rent Change', helper: 'Raises or lowers annual rent in the scenario only.' },
  { value: 'vacancy_change', label: 'Vacancy Change', helper: 'Models extra vacancy drag as a scenario-only income hit.' }
];

const scenarioValueTypeOptions: Array<{ value: ScenarioLineItemValueType; label: string }> = [
  { value: 'fixed', label: 'Fixed $' },
  { value: 'percent', label: '% Change' }
];

const recurrenceLabel = (item: ScenarioLineItem) => (
  item.recurrence === 'recurring'
    ? `Years ${item.startYear || item.year}${item.endYear && item.endYear !== (item.startYear || item.year) ? `-${item.endYear}` : ''}`
    : `Year ${item.year}`
);

const formatScenarioItemValue = (item: ScenarioLineItem) => (
  item.valueType === 'percent'
    ? `${formatNumber2(item.value)}%`
    : formatCurrency0(item.value)
);

const formatDelta = (value: number | null, suffix = '') => {
  if (value == null) return 'N/A';
  const prefix = value > 0 ? '+' : '';
  return `${prefix}${formatNumber2(value)}${suffix}`;
};

const deltaTone = (value: number | null, inverse = false) => {
  if (value == null || value === 0) return 'text-slate-300';
  const isPositive = value > 0;
  return inverse
    ? (isPositive ? 'text-rose-300' : 'text-emerald-300')
    : (isPositive ? 'text-emerald-300' : 'text-rose-300');
};

const RiskAnalysisView = (props: RiskAnalysisViewProps) => {
  const {
    modelResult,
    inputs,
    riskProfile,
    savedScenarios,
    auditLog,
    baseYear,
    activePropertyId,
    propertyName,
    properties = [],
    debts = {},
    debtLinks = [],
    debtEvents = [],
    onRiskProfileChange,
    onSaveScenario,
    onDeleteScenario,
    onRunScenario
  } = props;

  const [scenarioDraftId, setScenarioDraftId] = useState(null as string | null);
  const [scenarioName, setScenarioName] = useState('2027 Stress Test');
  const [scenarioItems, setScenarioItems] = useState([createEmptyScenarioLineItem(1)] as ScenarioLineItem[]);
  const [builderHighlight, setBuilderHighlight] = useState(false);
  const builderRef = useRef(null as HTMLDivElement | null);

  const summary = useMemo(() => {
    if (!inputs || !riskProfile || !modelResult) return null;
    return computeRiskSummary(inputs, riskProfile, modelResult);
  }, [inputs, riskProfile, modelResult]);

  const portfolioRankings = useMemo(() => {
    if (!properties.length) return null;
    const propertyLookup = Object.fromEntries(properties.map((property) => [property.id, property]));
    const scored = properties.map((property) => {
      const propertyModel = buildModel(
        property.currentYearInputs || property.inputs,
        property.riskProfile,
        buildDebtSnapshotForProperty(property.id, propertyLookup, debts, debtLinks, debtEvents),
        { baseYear: property.baseYear, historicalActuals: property.historicalActuals }
      );
      return {
        property,
        summary: computeRiskSummary(property.currentYearInputs || property.inputs, property.riskProfile, propertyModel)
      };
    });

    return {
      highestRisk: [...scored].sort((a, b) => b.summary.overallRiskScore - a.summary.overallRiskScore)[0],
      lowestResilience: [...scored].sort((a, b) => a.summary.resilienceScore - b.summary.resilienceScore)[0],
      highestExposure: [...scored].sort((a, b) => b.summary.capexNext24Months - a.summary.capexNext24Months)[0]
    };
  }, [properties, debts, debtLinks, debtEvents]);

  const availableYears = Math.max(1, modelResult?.proForma.length || 1);

  useEffect(() => {
    setScenarioItems((prev: ScenarioLineItem[]) => prev.map((item: ScenarioLineItem) => ({
      ...item,
      year: Math.min(availableYears, Math.max(1, Number(item.year || 1))),
      startYear: item.startYear == null ? null : Math.min(availableYears, Math.max(1, Number(item.startYear || 1))),
      endYear: item.endYear == null ? null : Math.min(availableYears, Math.max(1, Number(item.endYear || 1)))
    })));
  }, [availableYears]);

  const normalizedScenarioItems = useMemo(() => (
    scenarioItems.map((item: ScenarioLineItem) => ({
      ...item,
      year: Math.min(availableYears, Math.max(1, Number(item.year || 1))),
      startYear: item.startYear == null ? null : Math.min(availableYears, Math.max(1, Number(item.startYear || 1))),
      endYear: item.endYear == null ? null : Math.min(availableYears, Math.max(1, Number(item.endYear || 1)))
    }))
  ), [availableYears, scenarioItems]);

  const validScenarioItems = useMemo(() => normalizedScenarioItems.filter(isScenarioLineItemValid), [normalizedScenarioItems]);
  const analysisYear = useMemo(() => Math.min(availableYears, Math.max(1, getScenarioAnalysisYear(validScenarioItems))), [availableYears, validScenarioItems]);

  const draftScenario = useMemo((): SavedModelScenario | null => {
    if (!validScenarioItems.length) return null;
    return {
      id: scenarioDraftId || 'live-scenario-draft',
      propertyId: activePropertyId || 'unsaved-property',
      name: scenarioName.trim() || 'Untitled Scenario',
      items: validScenarioItems,
      summary: null,
      createdAt: Date.now(),
      lastRunAt: null
    };
  }, [activePropertyId, scenarioDraftId, scenarioName, validScenarioItems]);

  const liveStressResult = useMemo(() => {
    if (!inputs || !riskProfile || !modelResult || !draftScenario) return null;
    const stressedModel = buildModel(inputs, riskProfile, undefined, { baseYear, historicalActuals: [] }, { scenario: draftScenario });
    const stressedSummary = computeRiskSummary(inputs, riskProfile, stressedModel);
    const baselineYear = modelResult.proForma[analysisYear - 1];
    const stressedYear = stressedModel.proForma[analysisYear - 1];

    return {
      stressedModel,
      stressedSummary,
      scenarioSummary: {
        analysisYear,
        annualCashFlowDelta: (stressedYear?.operatingCf || 0) - (baselineYear?.operatingCf || 0),
        cocDelta: (stressedYear?.coc ?? null) !== null && (baselineYear?.coc ?? null) !== null
          ? (stressedYear?.coc || 0) - (baselineYear?.coc || 0)
          : null,
        irrDelta: (stressedYear?.irr ?? null) !== null && (baselineYear?.irr ?? null) !== null
          ? (stressedYear?.irr || 0) - (baselineYear?.irr || 0)
          : null
      }
    };
  }, [analysisYear, baseYear, draftScenario, inputs, modelResult, riskProfile]);

  const comparisonCards = useMemo(() => {
    if (!inputs || !riskProfile || !modelResult || !summary) return [];

    const baseCase = {
      id: 'base-case',
      label: 'Base Case',
      subtitle: 'Current model',
      items: [] as ScenarioLineItem[],
      analysisYear: 1,
      annualCashFlow: modelResult.proForma[0]?.operatingCf || 0,
      coc: modelResult.proForma[0]?.coc ?? null,
      irr: modelResult.proForma[0]?.irr ?? null,
      runwayMonths: summary.runwayMonths,
      overallRiskScore: summary.overallRiskScore,
      delta: {
        annualCashFlowDelta: 0,
        cocDelta: 0,
        irrDelta: 0
      }
    };

    const saved = savedScenarios.map((scenario) => {
      const scenarioYear = Math.min(availableYears, Math.max(1, getScenarioAnalysisYear(scenario.items || [])));
      const stressedModel = buildModel(inputs, riskProfile, undefined, { baseYear, historicalActuals: [] }, { scenario });
      const stressedSummary = computeRiskSummary(inputs, riskProfile, stressedModel);
      const baselineYear = modelResult.proForma[scenarioYear - 1];
      const stressedYear = stressedModel.proForma[scenarioYear - 1];

      return {
        id: scenario.id,
        label: scenario.name,
        subtitle: `${scenario.items.length} adjustment${scenario.items.length === 1 ? '' : 's'}`,
        items: scenario.items,
        analysisYear: scenarioYear,
        annualCashFlow: stressedYear?.operatingCf || 0,
        coc: stressedYear?.coc ?? null,
        irr: stressedYear?.irr ?? null,
        runwayMonths: stressedSummary.runwayMonths,
        overallRiskScore: stressedSummary.overallRiskScore,
        delta: {
          annualCashFlowDelta: (stressedYear?.operatingCf || 0) - (baselineYear?.operatingCf || 0),
          cocDelta: (stressedYear?.coc ?? null) !== null && (baselineYear?.coc ?? null) !== null
            ? (stressedYear?.coc || 0) - (baselineYear?.coc || 0)
            : null,
          irrDelta: (stressedYear?.irr ?? null) !== null && (baselineYear?.irr ?? null) !== null
            ? (stressedYear?.irr || 0) - (baselineYear?.irr || 0)
            : null
        }
      };
    });

    return [baseCase, ...saved];
  }, [availableYears, baseYear, inputs, modelResult, riskProfile, savedScenarios, summary]);
  const comparisonLookup = useMemo(() => Object.fromEntries(comparisonCards.map((card: any) => [card.id, card])), [comparisonCards]);

  if (!summary || !inputs || !riskProfile || !modelResult) {
    return (
      <div className="rounded-[2rem] border border-slate-700/60 bg-slate-900 p-8">
        <p className="text-sm text-slate-300">Save the property first, then the scenario workspace will open here for ongoing risk testing and comparison.</p>
      </div>
    );
  }

  const riskTone = getRiskTone(summary.overallRiskScore);

  const resetScenarioEditor = () => {
    setScenarioDraftId(null);
    setScenarioName('2027 Stress Test');
    setScenarioItems([createEmptyScenarioLineItem(1)]);
  };

  const loadScenarioIntoEditor = (scenario: SavedModelScenario) => {
    setScenarioDraftId(scenario.id);
    setScenarioName(scenario.name);
    setScenarioItems(
      scenario.items.length
        ? scenario.items.map((item) => ({ ...item }))
        : [createEmptyScenarioLineItem(1)]
    );
    setBuilderHighlight(true);
    requestAnimationFrame(() => {
      builderRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  useEffect(() => {
    if (!builderHighlight) return;
    const timeoutId = window.setTimeout(() => setBuilderHighlight(false), 2200);
    return () => window.clearTimeout(timeoutId);
  }, [builderHighlight]);

  const updateScenarioItem = (itemId: string, updates: Partial<ScenarioLineItem>) => {
    setScenarioItems((prev: ScenarioLineItem[]) => prev.map((item: ScenarioLineItem) => {
      if (item.id !== itemId) return item;
      const nextItem = { ...item, ...updates };
      if (updates.recurrence === 'one_time') {
        nextItem.startYear = null;
        nextItem.endYear = null;
      }
      if (updates.recurrence === 'recurring') {
        nextItem.startYear = nextItem.startYear || nextItem.year || 1;
        nextItem.endYear = nextItem.endYear || nextItem.startYear;
      }
      return nextItem;
    }));
  };

  return (
    <div className="animate-in fade-in slide-in-from-bottom-2 duration-500 space-y-8">
      <div className="rounded-[2rem] border border-slate-700/60 bg-gradient-to-br from-slate-900 to-slate-950 p-6 shadow-xl shadow-black/30 flex items-start justify-between gap-4">
        <div>
          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mb-2">Scenario Workspace</p>
          <h2 className="text-2xl font-black text-white tracking-tight">{propertyName || 'Saved Property'} Risk & Resilience</h2>
          <p className="text-xs text-slate-400 mt-2">The base model stays clean. Every what-if change now lives inside a saved scenario with its own adjustment log and comparison output.</p>
        </div>
        <span className={`inline-flex px-3 py-1 rounded-full border text-[9px] font-black uppercase tracking-widest ${riskTone.className}`}>{riskTone.label} Risk</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
        <div className="rounded-3xl border border-slate-700/60 bg-slate-900 p-5">
          <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-2">Overall Risk Score</p>
          <p className="text-3xl font-black text-white tracking-tighter">{formatNumber2(summary.overallRiskScore)}</p>
          <span className={`inline-flex mt-3 px-2.5 py-1 rounded-full border text-[9px] font-black uppercase tracking-widest ${riskTone.className}`}>{riskTone.label}</span>
        </div>
        <div className="rounded-3xl border border-slate-700/60 bg-slate-900 p-5">
          <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-2">Resilience Score</p>
          <p className="text-3xl font-black text-emerald-300 tracking-tighter">{formatNumber2(summary.resilienceScore)}</p>
          <p className="text-[10px] text-slate-500 mt-3">How much room the property has to absorb shocks.</p>
        </div>
        <div className="rounded-3xl border border-slate-700/60 bg-slate-900 p-5">
          <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-2">
            <InfoTooltip
              label="Recommended Reserves"
              content="Includes debt payments, property taxes, insurance, HOA dues, vacancy exposure, and near-term physical risk from the baseline condition model."
            />
          </p>
          <p className="text-2xl font-black text-white tracking-tight">{formatCurrency0(summary.recommendedReserve)}</p>
          <p className="text-[10px] text-slate-500 mt-3">Base-case reserve target before layering on any saved scenario.</p>
        </div>
        <div className="rounded-3xl border border-slate-700/60 bg-slate-900 p-5">
          <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-2">Upcoming Capital Exposure</p>
          <p className="text-2xl font-black text-amber-300 tracking-tight">{formatCurrency0(summary.capexNext24Months)}</p>
          <p className="text-[10px] text-slate-500 mt-3">Baseline physical exposure from systems and structural conditions only.</p>
        </div>
        <div className="rounded-3xl border border-slate-700/60 bg-slate-900 p-5">
          <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-2">Current Free Cash Flow</p>
          <p className={`text-2xl font-black tracking-tight ${summary.monthlyFreeCashFlow >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>{formatCurrency0(summary.monthlyFreeCashFlow)}/mo</p>
          <p className="text-[10px] text-slate-500 mt-3">Current baseline cushion before any stress scenario is applied.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-1 space-y-6">
          <div className="rounded-[2rem] border border-slate-700/60 bg-slate-900 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
                <ShieldCheck size={16} className="text-emerald-300" />
              </div>
              <div>
                <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Baseline Condition Summary</h3>
                <p className="text-xs text-slate-500 mt-2">The baseline tracks physical condition only. Simulated operating changes now belong in scenarios.</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="rounded-2xl border border-slate-700/70 bg-slate-950/60 p-4">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Major Systems Health</p>
                <p className="text-xl font-black text-white mt-2">{formatCurrency0(summary.majorSystemExposure24)}</p>
                <p className="text-[10px] text-slate-500 mt-2">Projected Tier 1 exposure inside 24 months.</p>
              </div>
              <div className="rounded-2xl border border-slate-700/70 bg-slate-950/60 p-4">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Structural Risk</p>
                <p className="text-xl font-black text-amber-300 mt-2">{formatCurrency0(summary.structuralExposure24)}</p>
                <p className="text-[10px] text-slate-500 mt-2">Baseline structural concerns feeding the model today.</p>
              </div>
              <div className="rounded-2xl border border-slate-700/70 bg-slate-950/60 p-4">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Stress Scenarios Saved</p>
                <p className="text-xl font-black text-white mt-2">{savedScenarios.length}</p>
                <p className="text-[10px] text-slate-500 mt-2">Saved overlays available for side-by-side comparison.</p>
              </div>
              <div className="rounded-2xl border border-slate-700/70 bg-slate-950/60 p-4">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Base Case Runway</p>
                <p className="text-xl font-black text-white mt-2">{summary.runwayMonths === Infinity ? 'Stable' : `${formatNumber2(summary.runwayMonths)} mos`}</p>
                <p className="text-[10px] text-slate-500 mt-2">Current liquidity runway before adding any scenario pressure.</p>
              </div>
            </div>
          </div>

          <div className="rounded-[2rem] border border-slate-700/60 bg-slate-900 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-2xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center">
                <Wallet size={16} className="text-cyan-300" />
              </div>
              <div>
                <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Reserve Readiness</h3>
                <p className="text-xs text-slate-500 mt-2">Base-case liquidity posture relative to what the asset is likely to ask for next.</p>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4">
              <div className="rounded-2xl border border-slate-700/70 bg-slate-950/60 p-4">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Current Reserves</p>
                <p className="text-2xl font-black text-white mt-2">{formatCurrency0(summary.currentReserve)}</p>
              </div>
              <div className="rounded-2xl border border-slate-700/70 bg-slate-950/60 p-4">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Target Reserves</p>
                <p className="text-2xl font-black text-emerald-300 mt-2">{formatCurrency0(summary.recommendedReserve)}</p>
              </div>
              <div className="rounded-2xl border border-slate-700/70 bg-slate-950/60 p-4">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Debt Pressure</p>
                <p className={`text-2xl font-black mt-2 ${summary.debtPressure > 0.55 ? 'text-rose-300' : 'text-emerald-300'}`}>{formatNumber2(summary.debtPressure * 100)}%</p>
              </div>
            </div>
          </div>
        </div>

        <div className="xl:col-span-2 space-y-6">
          <div className="rounded-[2rem] border border-slate-700/60 bg-slate-900 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-2xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
                <Wrench size={16} className="text-amber-300" />
              </div>
              <div>
                <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Upcoming Capital Exposure</h3>
                <p className="text-xs text-slate-500 mt-2">Major systems and structural concerns in the baseline model. Short-term operating stresses should be modeled as scenarios below.</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              <div className="rounded-2xl border border-slate-700/70 bg-slate-950/60 p-4">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Major Systems 12 Months</p>
                <p className="text-xl font-black text-white mt-2">{formatCurrency0(summary.majorSystemExposure12)}</p>
              </div>
              <div className="rounded-2xl border border-slate-700/70 bg-slate-950/60 p-4">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Structural 12 Months</p>
                <p className="text-xl font-black text-white mt-2">{formatCurrency0(summary.structuralExposure12)}</p>
              </div>
              <div className="rounded-2xl border border-slate-700/70 bg-slate-950/60 p-4">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">24 Month Exposure</p>
                <p className="text-xl font-black text-amber-300 mt-2">{formatCurrency0(summary.capexNext24Months)}</p>
              </div>
            </div>

            {summary.maintenanceRiskFlags.length > 0 && (
              <div className="mt-6 space-y-3">
                {summary.maintenanceRiskFlags.map((flag: string) => (
                  <div key={flag} className="rounded-2xl border border-amber-500/20 bg-amber-500/10 p-4 flex items-start gap-3">
                    <AlertTriangle size={16} className="text-amber-300 mt-0.5" />
                    <p className="text-sm text-slate-200 leading-relaxed">{flag}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          <RiskActionQueue actions={summary.actions} />

          <div
            ref={builderRef}
            className={`rounded-[2rem] border bg-slate-900 p-6 transition-all duration-500 ${builderHighlight ? 'border-emerald-500/60 shadow-[0_0_0_1px_rgba(16,185,129,0.25)]' : 'border-slate-700/60'}`}
          >
            <div className="flex items-start justify-between gap-4 mb-6">
              <div>
                <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Scenario Builder</h3>
                <p className="text-xs text-slate-500 mt-2">This is now the only place to simulate future changes. Build a scenario, save it, and compare it against the base case without ever modifying baseline inputs.</p>
                {scenarioDraftId && (
                  <p className="mt-3 inline-flex items-center rounded-full border border-amber-500/30 bg-amber-500/10 px-3 py-1 text-[10px] font-black uppercase tracking-widest text-amber-300">
                    Currently Editing: {scenarioName || 'Untitled Scenario'}
                  </p>
                )}
              </div>
              <span className="inline-flex px-3 py-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-[9px] font-black uppercase tracking-widest text-emerald-300">
                {scenarioDraftId ? 'Editing Saved Scenario' : 'Base Model Protected'}
              </span>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[1.1fr,1.4fr] gap-6">
              <div className="rounded-3xl border border-slate-700/70 bg-slate-950/60 p-5 space-y-4">
                <div>
                  <label className="block text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mb-2">Scenario Name</label>
                  <input
                    type="text"
                    value={scenarioName}
                    onChange={(e: any) => setScenarioName(e.target.value)}
                    className="w-full rounded-2xl border border-slate-700 bg-slate-900 px-4 py-3 text-sm font-medium text-white outline-none focus:border-emerald-500"
                    placeholder="2027 Stress Test"
                  />
                </div>

                <div className="space-y-3">
                  {scenarioItems.map((item: ScenarioLineItem, index: number) => (
                    <div key={item.id} className="rounded-2xl border border-slate-700/80 bg-slate-900/80 p-4 space-y-3">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Adjustment {index + 1}</p>
                          <p className="text-[11px] text-slate-500 mt-1">{scenarioTypeOptions.find((option) => option.value === item.type)?.helper}</p>
                        </div>
                        {scenarioItems.length > 1 && (
                          <button
                            type="button"
                            onClick={() => setScenarioItems((prev: ScenarioLineItem[]) => prev.filter((entry: ScenarioLineItem) => entry.id !== item.id))}
                            className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-[10px] font-black uppercase tracking-widest text-slate-300 transition-colors hover:border-slate-600 hover:text-white"
                          >
                            <X size={12} /> Remove
                          </button>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mb-2">Type</label>
                          <select
                            value={item.type}
                            onChange={(e: any) => updateScenarioItem(item.id, { type: e.target.value as ScenarioLineItemType })}
                            className="w-full rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-sm font-bold text-white outline-none focus:border-emerald-500"
                          >
                            {scenarioTypeOptions.map((option) => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mb-2">Label</label>
                          <input
                            type="text"
                            value={item.label}
                            onChange={(e: any) => updateScenarioItem(item.id, { label: e.target.value })}
                            className="w-full rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-sm font-medium text-white outline-none focus:border-emerald-500"
                            placeholder={getScenarioLineItemLabel(item)}
                          />
                        </div>

                        <div>
                          <label className="block text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mb-2">Amount Type</label>
                          <select
                            value={item.valueType}
                            onChange={(e: any) => updateScenarioItem(item.id, { valueType: e.target.value as ScenarioLineItemValueType })}
                            className="w-full rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-sm font-bold text-white outline-none focus:border-emerald-500"
                          >
                            {scenarioValueTypeOptions.map((option) => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mb-2">{item.valueType === 'percent' ? 'Percent Change' : 'Amount'}</label>
                          <input
                            type="number"
                            min={0}
                            step={item.valueType === 'percent' ? 0.5 : 100}
                            value={item.value}
                            onChange={(e: any) => updateScenarioItem(item.id, { value: Math.max(0, Number(e.target.value) || 0) })}
                            className="w-full rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-sm font-bold text-white outline-none focus:border-emerald-500"
                            placeholder={item.valueType === 'percent' ? '15' : '5000'}
                          />
                        </div>

                        <div>
                          <label className="block text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mb-2">Recurrence</label>
                          <select
                            value={item.recurrence}
                            onChange={(e: any) => updateScenarioItem(item.id, { recurrence: e.target.value as 'one_time' | 'recurring' })}
                            className="w-full rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-sm font-bold text-white outline-none focus:border-emerald-500"
                          >
                            <option value="one_time">One-Time</option>
                            <option value="recurring">Recurring</option>
                          </select>
                        </div>

                        {item.recurrence === 'one_time' ? (
                          <div>
                            <label className="block text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mb-2">Year</label>
                            <select
                              value={item.year}
                              onChange={(e: any) => updateScenarioItem(item.id, { year: Number(e.target.value) })}
                              className="w-full rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-sm font-bold text-white outline-none focus:border-emerald-500"
                            >
                              {Array.from({ length: availableYears }, (_, yearIndex) => yearIndex + 1).map((year) => (
                                <option key={year} value={year}>Year {year}</option>
                              ))}
                            </select>
                          </div>
                        ) : (
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mb-2">Start Year</label>
                              <select
                                value={item.startYear || 1}
                                onChange={(e: any) => updateScenarioItem(item.id, { startYear: Number(e.target.value), endYear: Math.max(Number(e.target.value), Number(item.endYear || e.target.value)) })}
                                className="w-full rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-sm font-bold text-white outline-none focus:border-emerald-500"
                              >
                                {Array.from({ length: availableYears }, (_, yearIndex) => yearIndex + 1).map((year) => (
                                  <option key={year} value={year}>Year {year}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mb-2">End Year</label>
                              <select
                                value={Math.max(item.startYear || 1, item.endYear || item.startYear || 1)}
                                onChange={(e: any) => updateScenarioItem(item.id, { endYear: Number(e.target.value) })}
                                className="w-full rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-sm font-bold text-white outline-none focus:border-emerald-500"
                              >
                                {Array.from({ length: availableYears }, (_, yearIndex) => yearIndex + 1).map((year) => (
                                  <option key={year} value={year} disabled={year < Number(item.startYear || 1)}>Year {year}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                        )}
                      </div>

                      <div>
                        <label className="block text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mb-2">Notes</label>
                        <input
                          type="text"
                          value={item.notes}
                          onChange={(e: any) => updateScenarioItem(item.id, { notes: e.target.value })}
                          className="w-full rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-sm font-medium text-white outline-none focus:border-emerald-500"
                          placeholder="Optional note for this assumption"
                        />
                      </div>
                    </div>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={() => setScenarioItems((prev: ScenarioLineItem[]) => [...prev, createEmptyScenarioLineItem(analysisYear)])}
                  className="w-full rounded-2xl border border-slate-700 bg-slate-900 px-4 py-3 text-[10px] font-black uppercase tracking-[0.2em] text-slate-300 transition-colors hover:border-slate-600 hover:text-white"
                >
                  <Plus size={12} className="inline mr-2" /> Add Adjustment
                </button>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={resetScenarioEditor}
                    className="rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-[10px] font-black uppercase tracking-[0.2em] text-slate-300"
                  >
                    Clear Scenario
                  </button>
                  <button
                    type="button"
                    disabled={!draftScenario || !liveStressResult}
                    onClick={() => {
                      if (!draftScenario || !liveStressResult) return;
                      onSaveScenario({
                        ...draftScenario,
                        createdAt: scenarioDraftId
                          ? (savedScenarios.find((scenario) => scenario.id === scenarioDraftId)?.createdAt || Date.now())
                          : Date.now(),
                        lastRunAt: null
                      });
                    }}
                    className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-[10px] font-black uppercase tracking-[0.2em] text-emerald-300 transition-colors disabled:opacity-40"
                  >
                    <Save size={12} className="inline mr-2" /> {scenarioDraftId ? 'Update Scenario' : 'Save Scenario'}
                  </button>
                </div>
              </div>

              <div className="rounded-3xl border border-slate-700/70 bg-slate-950/60 p-5">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Live Scenario Preview</p>
                    <h4 className="text-xl font-black text-white mt-2">{draftScenario?.name || 'Base Case Only'}</h4>
                    <p className="text-xs text-slate-500 mt-2">This preview compares the current draft against the base model in Year {analysisYear}. Preview results are recalculated when you run the saved scenario later.</p>
                  </div>
                  <span className="inline-flex rounded-full border border-slate-700 bg-slate-900 px-3 py-1 text-[9px] font-black uppercase tracking-widest text-slate-300">
                    Adjustment Log
                  </span>
                </div>

                {liveStressResult ? (
                  <div className="mt-6 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="rounded-2xl border border-slate-700/70 bg-slate-900/70 p-4">
                        <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Cash Flow Impact</p>
                        <p className={`mt-2 text-xl font-black ${deltaTone(liveStressResult.scenarioSummary.annualCashFlowDelta, true)}`}>{formatCurrency0(liveStressResult.scenarioSummary.annualCashFlowDelta)}</p>
                      </div>
                      <div className="rounded-2xl border border-slate-700/70 bg-slate-900/70 p-4">
                        <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">CoC Change</p>
                        <p className={`mt-2 text-xl font-black ${deltaTone(liveStressResult.scenarioSummary.cocDelta, true)}`}>{formatDelta(liveStressResult.scenarioSummary.cocDelta, '%')}</p>
                      </div>
                      <div className="rounded-2xl border border-slate-700/70 bg-slate-900/70 p-4">
                        <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">IRR Change</p>
                        <p className={`mt-2 text-xl font-black ${deltaTone(liveStressResult.scenarioSummary.irrDelta, true)}`}>{formatDelta(liveStressResult.scenarioSummary.irrDelta, '%')}</p>
                      </div>
                    </div>

                    <div className="rounded-2xl border border-slate-700/70 bg-slate-900/70 p-4">
                      <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Adjustment Log</p>
                      <div className="mt-3 space-y-3">
                        {validScenarioItems.map((item: ScenarioLineItem) => (
                          <div key={item.id} className="rounded-2xl border border-slate-700/70 bg-slate-950/60 p-4">
                            <p className="text-sm font-black text-white">{item.label.trim() || getScenarioLineItemLabel(item)}</p>
                            <p className="mt-1 text-[11px] text-slate-400">{scenarioTypeOptions.find((option) => option.value === item.type)?.label} • {formatScenarioItemValue(item)} • {recurrenceLabel(item)}</p>
                            <p className="mt-1 text-[11px] text-slate-500">{item.notes || 'No note added.'}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="mt-6 rounded-2xl border border-slate-700/70 bg-slate-900/70 p-4">
                    <p className="text-sm text-slate-300">Add at least one valid adjustment to preview a scenario against the base case.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-[2rem] border border-slate-700/60 bg-slate-900 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
            <ClipboardList size={16} className="text-emerald-300" />
          </div>
          <div>
            <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Scenario Comparison</h3>
            <p className="text-xs text-slate-500 mt-2">Base Case is always visible so you can compare saved scenarios side by side without wondering what changed.</p>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {comparisonCards.map((card: any) => (
            <div key={card.id} className="rounded-3xl border border-slate-700/70 bg-slate-950/60 p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-lg font-black text-white">{card.label}</p>
                  <p className="mt-1 text-[11px] text-slate-400">{card.subtitle}</p>
                </div>
                {card.id === 'base-case' ? (
                  <span className="inline-flex rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1 text-[9px] font-black uppercase tracking-widest text-emerald-300">Base Case</span>
                ) : (
                  <span className="inline-flex rounded-full border border-slate-700 bg-slate-900 px-2.5 py-1 text-[9px] font-black uppercase tracking-widest text-slate-300">Year {card.analysisYear}</span>
                )}
              </div>

              <div className="mt-4 grid grid-cols-1 gap-3">
                <div className="rounded-2xl border border-slate-700/70 bg-slate-900/70 p-3">
                  <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Annual Cash Flow</p>
                  <p className={`mt-2 text-xl font-black ${card.annualCashFlow >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>{formatCurrency0(card.annualCashFlow)}</p>
                  {card.id !== 'base-case' && (
                    <p className={`mt-2 text-[11px] ${deltaTone(card.delta.annualCashFlowDelta, true)}`}>{formatCurrency0(card.delta.annualCashFlowDelta)} vs base</p>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="rounded-2xl border border-slate-700/70 bg-slate-900/70 p-3">
                    <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">CoC</p>
                    <p className="mt-2 text-sm font-black text-white">{card.coc == null ? 'N/A' : `${formatNumber2(card.coc)}%`}</p>
                    {card.id !== 'base-case' && <p className={`mt-1 text-[10px] ${deltaTone(card.delta.cocDelta, true)}`}>{formatDelta(card.delta.cocDelta, '%')}</p>}
                  </div>
                  <div className="rounded-2xl border border-slate-700/70 bg-slate-900/70 p-3">
                    <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">IRR</p>
                    <p className="mt-2 text-sm font-black text-white">{card.irr == null ? 'N/A' : `${formatNumber2(card.irr)}%`}</p>
                    {card.id !== 'base-case' && <p className={`mt-1 text-[10px] ${deltaTone(card.delta.irrDelta, true)}`}>{formatDelta(card.delta.irrDelta, '%')}</p>}
                  </div>
                  <div className="rounded-2xl border border-slate-700/70 bg-slate-900/70 p-3">
                    <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Runway</p>
                    <p className="mt-2 text-sm font-black text-white">{card.runwayMonths === Infinity ? 'Stable' : `${formatNumber2(card.runwayMonths)}m`}</p>
                    {card.id !== 'base-case' && <p className="mt-1 text-[10px] text-slate-500">Risk {formatNumber2(card.overallRiskScore)}</p>}
                  </div>
                </div>
              </div>

              {card.items.length > 0 && (
                <div className="mt-4 rounded-2xl border border-slate-700/70 bg-slate-900/70 p-3">
                  <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Adjustment Log</p>
                  <div className="mt-3 space-y-2">
                    {card.items.map((item: ScenarioLineItem) => (
                      <div key={item.id} className="text-[11px] text-slate-300">
                        <span className="font-black text-white">{item.label.trim() || getScenarioLineItemLabel(item)}</span>
                        <span className="text-slate-400"> • {formatScenarioItemValue(item)} • {recurrenceLabel(item)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <div className="rounded-[2rem] border border-slate-700/60 bg-slate-900 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-2xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
              <Save size={16} className="text-amber-300" />
            </div>
            <div>
              <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Saved Scenarios</h3>
              <p className="text-xs text-slate-500 mt-2">Editable, persistent what-if definitions that never overwrite the baseline. Comparison results are recomputed when you run them.</p>
            </div>
          </div>
          <div className="space-y-3">
            {savedScenarios.length ? savedScenarios.map((scenario) => {
              const liveScenarioCard = comparisonLookup[scenario.id];
              return (
              <div key={scenario.id} className="rounded-2xl border border-slate-700/70 bg-slate-950/60 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-black text-white">{scenario.name}</p>
                    <p className="mt-2 text-[11px] text-slate-300">{scenario.items.length} adjustment{scenario.items.length === 1 ? '' : 's'}</p>
                    <p className="mt-1 text-[11px] text-slate-500">{scenario.lastRunAt ? `Last run ${new Date(scenario.lastRunAt).toLocaleString()}` : 'Saved definition. Run to recompute comparison output.'}</p>
                  </div>
                  <span className="inline-flex rounded-full border border-slate-700 bg-slate-900 px-2.5 py-1 text-[9px] font-black uppercase tracking-widest text-slate-300">
                    {liveScenarioCard ? `Year ${liveScenarioCard.analysisYear}` : 'Scenario'}
                  </span>
                </div>

                {liveScenarioCard && (
                  <div className="mt-3 rounded-2xl border border-slate-700/70 bg-slate-900/70 p-3">
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Cash Flow</p>
                        <p className={`mt-1 text-[11px] font-bold ${deltaTone(liveScenarioCard.delta.annualCashFlowDelta, true)}`}>{formatCurrency0(liveScenarioCard.delta.annualCashFlowDelta)}</p>
                      </div>
                      <div>
                        <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">CoC</p>
                        <p className={`mt-1 text-[11px] font-bold ${deltaTone(liveScenarioCard.delta.cocDelta, true)}`}>{formatDelta(liveScenarioCard.delta.cocDelta, '%')}</p>
                      </div>
                      <div>
                        <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">IRR</p>
                        <p className={`mt-1 text-[11px] font-bold ${deltaTone(liveScenarioCard.delta.irrDelta, true)}`}>{formatDelta(liveScenarioCard.delta.irrDelta, '%')}</p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="mt-3 rounded-2xl border border-slate-700/70 bg-slate-900/70 p-3">
                  <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Adjustment Log</p>
                  <div className="mt-3 space-y-2">
                    {scenario.items.map((item) => (
                      <div key={item.id} className="text-[11px] text-slate-300">
                        <span className="font-black text-white">{item.label.trim() || getScenarioLineItemLabel(item)}</span>
                        <span className="text-slate-400"> • {formatScenarioItemValue(item)} • {recurrenceLabel(item)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => onRunScenario(scenario.id)}
                    className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-[10px] font-black uppercase tracking-widest text-emerald-300"
                  >
                    Run
                  </button>
                  <button
                    type="button"
                    onClick={() => loadScenarioIntoEditor(scenario)}
                    className="rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-[10px] font-black uppercase tracking-widest text-slate-300"
                  >
                    <Pencil size={12} className="inline mr-2" /> Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const duplicateScenario: SavedModelScenario = {
                        ...scenario,
                        id: crypto.randomUUID(),
                        name: `${scenario.name} Copy`,
                        items: scenario.items.map((item) => ({ ...item, id: crypto.randomUUID() })),
                        createdAt: Date.now(),
                        lastRunAt: null
                      };
                      onSaveScenario(duplicateScenario);
                      loadScenarioIntoEditor(duplicateScenario);
                    }}
                    className="rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-[10px] font-black uppercase tracking-widest text-slate-300"
                  >
                    <Copy size={12} className="inline mr-2" /> Duplicate
                  </button>
                  <button
                    type="button"
                    onClick={() => onDeleteScenario(scenario.id)}
                    className="rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-[10px] font-black uppercase tracking-widest text-slate-300"
                  >
                    Delete
                  </button>
                </div>
              </div>
              );
            }) : (
              <div className="rounded-2xl border border-slate-700/70 bg-slate-950/60 p-4">
                <p className="text-sm text-slate-300">No saved scenarios yet. Base Case is still available above for comparison.</p>
              </div>
            )}
          </div>
        </div>

        <div className="rounded-[2rem] border border-slate-700/60 bg-slate-900 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-2xl bg-slate-800 border border-slate-700 flex items-center justify-center">
              <ClipboardList size={16} className="text-slate-300" />
            </div>
            <div>
              <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Change Log</h3>
              <p className="text-xs text-slate-500 mt-2">This explains why results changed and whether the source was baseline or scenario.</p>
            </div>
          </div>
          <div className="space-y-3 max-h-[28rem] overflow-y-auto pr-1">
            {auditLog.length ? auditLog.map((entry) => (
              <div key={entry.id} className="rounded-2xl border border-slate-700/70 bg-slate-950/60 p-4">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">{entry.kind.replace(/_/g, ' ')}</p>
                <p className="mt-2 text-sm font-black text-white">{entry.title}</p>
                <p className="mt-2 text-[11px] leading-relaxed text-slate-400">{entry.detail}</p>
                <p className="mt-2 text-[10px] text-slate-500">{new Date(entry.createdAt).toLocaleString()}</p>
              </div>
            )) : (
              <div className="rounded-2xl border border-slate-700/70 bg-slate-950/60 p-4">
                <p className="text-sm text-slate-300">No audit records yet.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <PropertyRiskProfileEditor riskProfile={riskProfile} onChange={onRiskProfileChange} />

      {portfolioRankings && (
        <div className="rounded-[2rem] border border-slate-700/60 bg-slate-900 p-6">
          <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mb-5">Portfolio Signals</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="rounded-2xl border border-slate-700/70 bg-slate-950/60 p-4">
              <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-2 flex items-center gap-2"><AlertTriangle size={12} className="text-rose-400" /> Highest Risk Property</p>
              <p className="text-sm font-black text-white">{portfolioRankings.highestRisk.property.name}</p>
              <p className="text-[11px] text-rose-300 mt-1">Risk {formatNumber2(portfolioRankings.highestRisk.summary.overallRiskScore)}</p>
            </div>
            <div className="rounded-2xl border border-slate-700/70 bg-slate-950/60 p-4">
              <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-2 flex items-center gap-2"><ShieldCheck size={12} className="text-amber-400" /> Lowest Resilience</p>
              <p className="text-sm font-black text-white">{portfolioRankings.lowestResilience.property.name}</p>
              <p className="text-[11px] text-amber-300 mt-1">Score {formatNumber2(portfolioRankings.lowestResilience.summary.resilienceScore)}</p>
            </div>
            <div className="rounded-2xl border border-slate-700/70 bg-slate-950/60 p-4">
              <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-2 flex items-center gap-2"><Building2 size={12} className="text-cyan-300" /> Highest 24-Month Exposure</p>
              <p className="text-sm font-black text-white">{portfolioRankings.highestExposure.property.name}</p>
              <p className="text-[11px] text-cyan-300 mt-1">{formatCurrency0(portfolioRankings.highestExposure.summary.capexNext24Months)}</p>
            </div>
          </div>
        </div>
      )}

      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 flex items-center gap-2">
        <TrendingUp size={12} className="text-emerald-400" />
        One simulation path now exists here: scenarios. Baseline condition data stays separate so results are easier to trust.
      </div>
    </div>
  );
};

export default RiskAnalysisView;
